import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { convertBoolDiffValue } from "./valueFunctions";
import firstCharToUpper from "../helpers/stringFormat";
import { getIconFromDriverName } from "./iconFunctions";
import RevenueValueDriverSalesForm from "./RevenueValueDriverSalesForm";
import { getAllOutcomes } from "./valueFunctions";

const RevenueValueDriverSales = ({ id, valueModel, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [editSalesDriverId, setEditSalesDriverId] = useState(null);
    const [deleteSalesDriverPopupOpen, setDeleteSalesDriverPopupOpen] = useState(false);
    const [deleteSalesDriverId, setDeleteSalesDriverId] = useState(null);

    const handleResetSales = () => {
        setEditSalesDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const openDeleteSalesDriver = (id) => {
        setDeleteSalesDriverId(id)
        setDeleteSalesDriverPopupOpen(true)
    }

    const editSalesDriver = (id) => {
        setEditSalesDriverId(id)
    }

    const onSubmitSales = async (values, driver, { setSubmitting, setErrors }) => {
        values.id = editSalesDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.driver = driver
        values.annual_contract_value = values.annual_contract_value.toString().replace(/,/g, "");
        values.transaction_value = values.transaction_value.toString().replace(/,/g, "");
        let success = false

        if (editSalesDriverId) {
            const result = await dispatch(editDriver('sales', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditSalesDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('sales', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const salesDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'conversion_increase', label: 'Conversion change', format: (value) => createConversionChip(value), minWidth: 150, action: false },
        // { id: 'revenue_model', label: 'Revenue model', format: (value) => formatString(value), minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createSalesDriverData = (id, outcome, driver_name, conversion_increase, revenue_model, positive_driver, actions) => {
        // Create a shallow copy of conversion_increase to avoid mutating the original object
        const conversionIncreaseCopy = { ...conversion_increase };

        // Modify the copy
        Object.keys(conversionIncreaseCopy).forEach(type => {
            conversionIncreaseCopy[type] = `${conversionIncreaseCopy[type]}% ${positive_driver ? 'increase' : 'decrease'}`;
        });

        return { id, outcome, driver_name, conversion_increase: conversionIncreaseCopy, revenue_model, positive_driver, actions };
    }

    const createConversionChip = (values) => {
        return (
            <React.Fragment>
                {
                    Object.entries(values).map(([key, value]) => {

                        if (!value.startsWith('0%')) {
                            return (
                                <Chip key={key} label={firstCharToUpper(key) + ': ' + value} sx={{ mr: 1, mb: 0.5 }} />
                            )
                        }
                        else {
                            return null;
                        }
                    })
                }
            </React.Fragment>
        )
    }

    const salesDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editSalesDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteSalesDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const salesDriverRows = valueModel?.revenue_value_driver.sales_driven.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createSalesDriverData(driver.id, driver.outcome, driver.driver_name, driver.conversion_increase, driver.revenue_model, driver.positive_driver, salesDriverActions(driver.id))
        )
    })

    const salesDriverTable = () => {
        return (
            <React.Fragment>
                {/* <Divider /> */}
                <Table
                    columns={salesDriverColumns}
                    rows={salesDriverRows}
                    selectedId={editSalesDriverId}
                />
            </React.Fragment>
        )
    }

    const salesDriverFormActions = () => {
        return (
            <React.Fragment>
                {editSalesDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editSalesDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetSales}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Marketing and Sales
                </Typography>
            </Stack>
            <RevenueValueDriverSalesForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                editSalesDriverId={editSalesDriverId}
                editSalesDriver={valueModel?.revenue_value_driver.sales_driven.find(salesDriver => salesDriver.id === editSalesDriverId)}
                formActions={salesDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitSales(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {salesDriverTable()}
            <DeletePopup open={deleteSalesDriverPopupOpen} onClose={() => setDeleteSalesDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('sales', id, deleteSalesDriverId))} />
        </Box>
    )

}

export default RevenueValueDriverSales